import dayjs from 'dayjs';
import { forwardRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelProps,
  Legend,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import i18n from '../../../../../../base-dictionary';
import { useStrategyStore, useThroughputTabStore } from '../../../../../../store/strategy-store/strategy-store';
import {
  setPerformanceActivePanel,
  setThroughputSelectedDate,
  setThroughputSelectedTeam,
} from '../../../../../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../../../../../store/strategy-store/strategy-store.type';
import { skyLight, skyLightest } from '../../../../../../styles/design-tokens';
import { getMetricLabel } from '../../../../strategy-v2.helpers';
import { ContentPlaceholder } from '../../../components/content-placeholder/content-placeholder';
import { CHART_COLORS } from '../../../initiative-performance.constants';
import { ContributingEpicsChartLegend } from '../contributing-epics-chart/contributing-epics-chart-legend';
import { ContributingEpicsChartTooltip } from '../contributing-epics-chart/contributing-epics-chart-tooltip';
import { useChartData } from './contributing-teams-chart.hooks';

export const ContributingTeamsChart = forwardRef<HTMLDivElement>((_, ref) => {
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const selectedTeams = useThroughputTabStore((state) => state.selectedTeams);
  const metric = useThroughputTabStore((state) => state.metric);

  const barColors = Object.fromEntries(
    selectedTeams.map((team, index) => {
      return [team.name, CHART_COLORS[index]];
    }),
  );

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const chartData = useChartData();
  const hasData = Array.isArray(chartData) && chartData.length > 0;

  const renderCustomBarLabel = (props: LabelProps) => {
    const { x, y, width, value } = props;

    if (value === 0) {
      return <div />;
    }

    return (
      <text
        x={Number(x) + Number(width) / 2}
        y={Number(y) - 5}
        fill="#333"
        textAnchor="middle"
        fontSize={10}
        fontWeight="500"
      >
        {value}
      </text>
    );
  };

  if (!initiative || !hasData) {
    return <ContentPlaceholder />;
  }

  if (selectedTeams.length === 0) {
    return <ContentPlaceholder icon="filter_list" text={i18n.t('strategy.filters.no_filters_applied')} />;
  }

  return (
    <div ref={ref} style={{ width: '100%', height: '100%', minHeight: 160 }}>
      <ResponsiveContainer width="100%" height="100%" minHeight={160}>
        <BarChart data={chartData} margin={{ top: 20 }} barGap={2} barCategoryGap="10%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={skyLight} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            tickFormatter={(value) => dayjs(value).format('MMM, YYYY')}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            label={{
              value: `${getMetricLabel(metric)} ${i18n.t('strategy.initiative_performance.chart_labels.per_month')}`,
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
            }}
          />
          <Tooltip content={<ContributingEpicsChartTooltip barColors={barColors} />} cursor={{ fill: skyLightest }} />
          <Legend
            content={
              <ContributingEpicsChartLegend
                hoveredItem={hoveredItem}
                setHoveredItem={setHoveredItem}
                barColors={barColors}
              />
            }
            verticalAlign="bottom"
            height={36}
          />
          {selectedTeams.map((team) => {
            const color = barColors[team.name];

            return (
              <Bar
                key={team.name}
                dataKey={team.name}
                label={renderCustomBarLabel}
                width={10}
                animationDuration={0}
                background={{ fill: color, opacity: 0.1 }}
                onClick={(chartData) => {
                  setThroughputSelectedTeam(team);
                  setThroughputSelectedDate(dayjs((chartData as any).date, 'YYYY-MM').toDate());
                  setPerformanceActivePanel(Panel.Side);
                }}
                shape={
                  <Rectangle
                    fill={color}
                    onMouseEnter={() => setHoveredItem(team.name)}
                    onMouseLeave={() => setHoveredItem(null)}
                    opacity={hoveredItem === null || hoveredItem === team.name ? 0.8 : 0.3}
                    style={{
                      cursor: hoveredItem === team.name ? 'pointer' : 'default',
                    }}
                  />
                }
              />
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
});
